import BaseModal from 'components/Modals/BaseModal';
import { KeyboardEvent, useEffect, useRef, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { ACTION_LABELS } from '../config/ServiceManager.config';
import styles from '../css/ServiceManager.module.css';
import type { ServiceAction } from '../types/ServiceManager.types';

const REASON_MAX_LENGTH = 500;

interface ServiceActionModalProps {
  show: boolean;
  action?: ServiceAction;
  serviceName?: string;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}

/**
 * Confirmation modal shown before performing a service action.
 * Requires the user to provide a reason (max 500 characters) before confirming.
 */
export default function ServiceActionModal({
  show,
  action,
  serviceName,
  onConfirm,
  onCancel,
}: ServiceActionModalProps) {
  const [reason, setReason] = useState('');
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (show) {
      setReason('');
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [show]);

  const trimmedReason = reason.trim();
  const isReasonValid = trimmedReason.length > 0;

  const handleConfirm = () => {
    if (!isReasonValid) return;
    onConfirm(trimmedReason);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleConfirm();
    }
  };

  const config = action
    ? ACTION_LABELS[action]
    : { label: action || '', variant: 'primary' as const };
  const { label, variant } = config;

  return (
    <BaseModal
      show={show}
      handleShow={onCancel}
      size="sm"
      title={`${label} Service`}
      contentStyle={{}}
      footer={
        <>
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant={variant} onClick={handleConfirm} disabled={!isReasonValid}>
            {label}
          </Button>
        </>
      }
    >
      <p className="text-theme-muted mb-3">
        {label} <span className="text-theme fw-semibold">{serviceName}</span>?
      </p>
      <Form.Group>
        <Form.Label className="text-theme">
          Reason <span className="text-danger">*</span>
        </Form.Label>
        <Form.Control
          ref={inputRef}
          as="textarea"
          rows={2}
          placeholder="Enter a reason..."
          value={reason}
          maxLength={REASON_MAX_LENGTH}
          onChange={(e) => setReason(e.target.value)}
          onKeyDown={handleKeyDown}
          className={styles.reasonTextarea}
        />
        <div className="d-flex justify-content-between mt-1" style={{ fontSize: '0.75rem' }}>
          <span className="text-danger">{isReasonValid ? '' : 'A reason is required.'}</span>
          <span className="text-theme-muted">
            {reason.length} / {REASON_MAX_LENGTH}
          </span>
        </div>
      </Form.Group>
    </BaseModal>
  );
}
